/**
 * reinsertModuleIntoLiveDoc — rewrite a single module's section(s) in the live
 * Word document from the server's current persisted draft.
 *
 * Used after a regulatory audit: the audit mutates the persisted ``draft_full``
 * (inserting ``[REGULATOR FLAG: …]`` markers at the anchored paragraphs) but the
 * open document is not touched. Calling this re-projects the module's sections so
 * the markers appear inline at the right location — which also lets the review
 * tab's jump-to-issue (``selectTextMarker``) find them.
 *
 * Mirrors the per-module pass of ``loadRunIntoLiveDoc`` /
 * ``useWordWriteQueue.handleProgressEvent`` exactly (sanitize →
 * splitDraftBySections → insertSectionContent → citation pipeline) so the result
 * is identical to a freshly-streamed section. Unlike ``loadRunIntoLiveDoc`` it
 * does NOT scaffold or clear the body — it edits one module in place.
 *
 * Best-effort + non-destructive: ``insertSectionContent`` is a no-op (returns
 * false) when Office isn't available or the heading isn't present, so when no
 * document is loaded this resolves to ``{ sectionsWritten: 0 }`` without error.
 */

import { getSectionContent, renderSectionFragments } from "../../api/client";
import { filterContentLines } from "../../services/formatter/sectionHeaderLogic";
import { splitDraftBySections } from "../../services/formatter/sectionSplitter";
import {
  insertSectionContentDetailed,
  refreshLiveDocFields,
  stripRegulatorFlagsInTableCells,
} from "../../services/wordWriter";
import { refreshCitationMarkerEvents } from "./citationMarkerEvents";
import { wrapCitationsInParagraphs } from "./citationMarkerWriter";
import { runPostSectionCitationPipeline } from "./citationPipeline";
import { sanitizeDraftForWord, stripRegulatorMarkers } from "./draftSanitizer";
import { isUnnumberedSection } from "../sectionLabel";
import { reportWordFailure } from "../../services/wordOpsLogger";

export interface ReinsertModuleResult {
  sectionsWritten: number;
  /** How many section writes were actually ATTEMPTED. Zero means the module had
   *  nothing to project (every declared section empty, or all of its prose
   *  routed to children that this module does not itself own) — which is a
   *  no-op, NOT a failure. Callers that only saw `sectionsWritten` reported a
   *  red "failed" tick for a module where nothing was wrong.
   *
   *  Optional so that a stub returning only `sectionsWritten` still satisfies
   *  this contract; `outcomeFromSectionsWritten` reads `undefined` as "a write
   *  was attempted", i.e. exactly the behaviour that predates this field. The
   *  real implementation always sets it. */
  sectionsAttempted?: number;
}

export interface ReinsertModuleOptions {
  /** Strip every ``[REGULATOR FLAG: …]`` marker (and the trailing
   *  recommendations block) from the projected draft. Used for the "clean
   *  slate" re-projection right before a re-audit — the server draft still
   *  carries the old flags until the audit's own strip-and-reinsert runs. */
  stripRegulatorFlags?: boolean;
  /** Custom-submission override: the writer's REAL heading number for this
   *  module. A custom module's server ``section_number`` is the synthetic
   *  ``CUSTOM-N`` id, which never matches a heading in the writer's document,
   *  so the default number-keyed insert writes 0 sections and the audit markers
   *  never appear (then reconcile auto-resolves them). When set, the module's
   *  single body is written at THIS heading number instead — bypassing the
   *  ``## N.x`` split (a custom module is one heading, its ``draft_full`` is the
   *  body). Safe only after the live→SSOT sync, because it REPLACES the body. */
  customSectionNumber?: string;
  /** Custom-submission override: the writer's heading TITLE. A custom heading
   *  renders its title with NO leading number and no bookmark, so the number
   *  alone (``customSectionNumber``) often can't locate it — the title is passed
   *  to ``insertSectionContent`` (and the table strip) as the resolver that
   *  actually finds the heading. Undefined for template runs. */
  customSectionTitle?: string;
}

/** Everything ``applyModuleReinsert`` needs, fetched without touching Word. */
export interface ModuleReinsertPlan {
  moduleId: string;
  writeJobs: Array<{ secNum: string; chunk: string; secTitle?: string }>;
  /** Body-only .docx per write job (index-aligned); null → markdown renderer. */
  fragments: Array<string | null>;
  allNums: string[];
  options: ReinsertModuleOptions;
}

/** Network half of a re-projection: the module's current draft plus its
 *  server-rendered fragments. Touches no Word state, so it must run OUTSIDE the
 *  Word write queue — holding the queue across these round trips stalled every
 *  Save, Load and cursor probe behind network latency. */
export async function prepareModuleReinsert(
  runId: string,
  moduleId: string,
  options: ReinsertModuleOptions = {},
): Promise<ModuleReinsertPlan> {
  const resp = await getSectionContent(runId, moduleId);
  const sections = resp?.sections ?? [];

  // Every numbered section this module declares — used to distribute the draft's
  // "## N.x" blocks into the numbered skeleton the same way the live path does.
  const allNums = sections
    .map((s) => s.section_number)
    .filter((n): n is string => !!n && n !== "TITLE");

  const writeJobs: Array<{ secNum: string; chunk: string; secTitle?: string }> = [];

  // Custom-submission fast path: one module == one real heading. The server
  // ``section_number`` is ``CUSTOM-N`` (never matches a heading), so target the
  // writer's real heading (by number AND title — a custom heading often carries
  // no number, so the title is what actually resolves it) and write the whole
  // (marker-annotated) body there, skipping the ``## N.x`` split which a custom
  // body doesn't carry.
  if (options.customSectionNumber || options.customSectionTitle) {
    for (const sec of sections) {
      if (!sec.draft_content) continue;
      const source = options.stripRegulatorFlags
        ? stripRegulatorMarkers(sec.draft_content)
        : sec.draft_content;
      const cleaned = sanitizeDraftForWord(source);
      if (!cleaned) continue;
      writeJobs.push({
        secNum: options.customSectionNumber ?? "",
        secTitle: options.customSectionTitle,
        chunk: cleaned,
      });
    }
  } else {
    for (const sec of sections) {
      if (!sec.section_number || sec.section_number === "TITLE" || !sec.draft_content) continue;
      const source = options.stripRegulatorFlags
        ? stripRegulatorMarkers(sec.draft_content)
        : sec.draft_content;
      const cleaned = sanitizeDraftForWord(source);
      if (!cleaned) continue;
      const chunks = Object.entries(
        splitDraftBySections(cleaned, allNums, sec.section_number),
      ).filter(([, c]) => c.trim().length > 0);
      for (const [secNum, chunk] of chunks) {
        // A private template's unnumbered heading can't be found by number
        // prefix; its title backs up the bookmark (empty set for public types).
        const secTitle =
          isUnnumberedSection(secNum) && secNum === sec.section_number ? sec.section_title : undefined;
        writeJobs.push({ secNum, chunk, ...(secTitle ? { secTitle } : {}) });
      }
    }
  }

  // One server round trip renders every chunk as a body-only .docx, so each
  // section goes into Word with a single file insert rather than the
  // per-paragraph render (~1 min a section on Word for the web). The heading
  // line is filtered out first, exactly as the markdown renderer does, because
  // the live doc already owns the heading. Any failure here just means the
  // renderer path below runs, as it always did.
  const fragments = await fetchFragments(runId, moduleId, writeJobs);
  return { moduleId, writeJobs, fragments, allNums, options };
}

/** Word half of a re-projection. Run inside the Word write queue. */
export async function applyModuleReinsert(plan: ModuleReinsertPlan): Promise<ReinsertModuleResult> {
  const { moduleId, writeJobs, fragments, allNums, options } = plan;

  let written = 0;
  for (const [idx, { secNum, chunk, secTitle }] of writeJobs.entries()) {
    try {
      const fragmentBase64 = fragments[idx] ?? undefined;
      const { ok, afterInsertRan } = await insertSectionContentDetailed({
        sectionNumber: secNum,
        sectionTitle: secTitle,
        content: chunk,
        scrollAfterInsert: false,
        ...(fragmentBase64 ? { fragmentBase64 } : {}),
        // Wrap citations on the inserted paragraphs inside the insert's own
        // Word.run — no second section lookup (fragment path only).
        afterInsert: async (context, paragraphs) => {
          await wrapCitationsInParagraphs(context, secNum, paragraphs, { textLoaded: true, fresh: true });
        },
      });
      if (ok) {
        written += 1;
        // Re-wrap citation markers so the rewritten section behaves identically
        // to a freshly-streamed one (clickable ``[N]``). Swallows its own errors.
        if (afterInsertRan) {
          await refreshCitationMarkerEvents();
        } else {
          await runPostSectionCitationPipeline(secNum);
        }
      }
    } catch (err) {
      reportWordFailure("reinsertModuleIntoLiveDoc.insertSectionContent", err);
      console.warn(
        `[reinsertModuleIntoLiveDoc] insertSectionContent failed for §${secNum} (non-fatal):`,
        err,
      );
    }
  }

  // Caption numbers (SEQ), "Section x" / "Table x-y" references (REF) and
  // lists of tables / figures (TOC) in the rewritten sections come from the
  // server render with the drafter's numbers; let Word recompute them.
  if (written > 0) await refreshLiveDocFields();

  // Every attempted write missed. Historically this surfaced only as a bland
  // "0 sections written", which is what let a server/client section-number
  // namespace mismatch hide for so long: the module's content was fine and the
  // headings were present, but the numbers named in this payload existed in no
  // document. Name both sides so the mismatch is visible at a glance. Mirrors
  // the Phase-0 probe in loadRunIntoLiveDoc.
  if (writeJobs.length > 0 && written === 0) {
    console.warn(
      `[reinsertModuleIntoLiveDoc] namespace check for ${moduleId}: attempted ` +
        `${writeJobs.length} write(s) and NONE resolved to a heading. ` +
        `Section numbers from the server: ${JSON.stringify(writeJobs.map((j) => j.secNum))}. ` +
        "If these look like module ids rather than skeleton section numbers, the " +
        "server's section-response builder is the suspect, not the Word document.",
    );
  }

  // The section re-render above clears inline PROSE flags but never touches
  // table content (its paragraph-delete boundary would corrupt tables), so a
  // ``[REGULATOR FLAG: …]`` sitting in a table cell survives it. On the
  // clean-slate strip pass, sweep those cell markers out too so the audit
  // starts from a doc with NO regulatory flags — including inside tables.
  const isCustom = !!(options.customSectionNumber || options.customSectionTitle);
  const tableStripTargets = isCustom
    ? [{ number: options.customSectionNumber, title: options.customSectionTitle }]
    : allNums;
  if (options.stripRegulatorFlags && tableStripTargets.length > 0) {
    try {
      await stripRegulatorFlagsInTableCells(tableStripTargets);
    } catch (err) {
      reportWordFailure("reinsertModuleIntoLiveDoc.flagStrip", err);
      console.warn("[reinsertModuleIntoLiveDoc] table-cell flag strip failed (non-fatal):", err);
    }
  }

  return { sectionsWritten: written, sectionsAttempted: writeJobs.length };
}

/** Fetch + write in one call (Word work only after the fetch completes). */
export async function reinsertModuleIntoLiveDoc(
  runId: string,
  moduleId: string,
  options: ReinsertModuleOptions = {},
): Promise<ReinsertModuleResult> {
  return applyModuleReinsert(await prepareModuleReinsert(runId, moduleId, options));
}

/** Per-module chain of in-flight prepares, so two re-projections of the same
 *  module enqueue their writes in request order (an older, slower fetch must
 *  not land after a newer one). Different modules don't wait on each other. */
const _prepareChains = new Map<string, Promise<void>>();

/**
 * Re-project a module with the fetch OUTSIDE the Word write lock and only the
 * Office.js work inside it. ``apply`` defaults to {@link applyModuleReinsert};
 * callers wrap it to re-check conditions at write time (e.g. a Load started
 * while the job waited in the queue).
 */
export function queueModuleReinsert<T = ReinsertModuleResult>(
  runId: string,
  moduleId: string,
  options: ReinsertModuleOptions,
  enqueue: <R>(fn: () => Promise<R>) => Promise<R>,
  apply: (plan: ModuleReinsertPlan) => Promise<T> = applyModuleReinsert as unknown as (
    plan: ModuleReinsertPlan,
  ) => Promise<T>,
): Promise<T> {
  const key = `${runId}:${moduleId}`;
  const prev = _prepareChains.get(key) ?? Promise.resolve();
  const prepared = prev.then(() => prepareModuleReinsert(runId, moduleId, options));
  // Enqueued synchronously in the same continuation, so the next prepare for
  // this module (which waits on ``marker``) always enqueues after this one.
  const result = prepared.then((plan) => enqueue(() => apply(plan)));
  const marker = prepared.then(
    () => undefined,
    () => undefined,
  );
  _prepareChains.set(key, marker);
  void marker.then(() => {
    if (_prepareChains.get(key) === marker) _prepareChains.delete(key);
  });
  return result;
}

/** Body-only .docx per write job (index-aligned), or nulls when the server
 *  can't render them — the caller then renders the markdown itself. */
async function fetchFragments(
  runId: string,
  moduleId: string,
  writeJobs: Array<{ secNum: string; chunk: string }>,
): Promise<Array<string | null>> {
  const none = writeJobs.map(() => null);
  if (writeJobs.length === 0) return none;
  try {
    const t0 = Date.now();
    const resp = await renderSectionFragments(
      runId,
      moduleId,
      writeJobs.map(({ secNum, chunk }) => ({
        section_number: secNum,
        markdown: filterContentLines(chunk.split("\n"), secNum).join("\n"),
      })),
    );
    const fragments = resp?.fragments ?? [];
    if (fragments.length !== writeJobs.length) return none;
    console.log(
      `[reinsertModuleIntoLiveDoc] ${moduleId}: ${fragments.filter((f) => f.docx_base64).length}/` +
        `${writeJobs.length} fragment(s) rendered in ${Date.now() - t0}ms`,
    );
    return fragments.map((f) => f.docx_base64 ?? null);
  } catch (err) {
    reportWordFailure("reinsertModuleIntoLiveDoc.fragmentRender", err);
    console.warn(`[reinsertModuleIntoLiveDoc] fragment render failed for ${moduleId} — using the renderer`, err);
    return none;
  }
}
